import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { formatDistanceToNow } from "@/utils/dateUtils";
import Card from "@/components/common/Card";
import StatCard from "@/components/common/StatCard";
import Badge from "@/components/common/Badge";
import ActionMenu from "@/components/common/ActionMenu";
import SearchInput from "@/components/common/SearchInput";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import RowIcon from "@/components/common/RowIcon";
import AnimatedNumber from "@/components/common/AnimatedNumber";
import { usePageTitle } from "@/components/common/AppShell";
import { DEFAULT_AUDIENCE_ICON, nameOr } from "@/utils/itemIcon";
import { getRowIcon } from "@/utils/getRowIcon";
import {
  DEFAULT_SEGMENT_TYPE,
  SEGMENT_TYPE,
  getSegmentTypeIcon,
  getSegmentTypeLabel,
} from "@/utils/segmentTypeIcon";

const TYPE_TABS = [
  { id: "all", label: "All audiences", type: null },
  { id: "saved", label: "Saved audiences", type: SEGMENT_TYPE.SAVED_SEGMENT },
  {
    id: "lookalike",
    label: "Look-alike audiences",
    type: SEGMENT_TYPE.LOOKALIKE,
  },
  { id: "custom", label: "Custom audiences", type: SEGMENT_TYPE.PREBUILT },
  {
    id: "ai",
    label: "AI audiences",
    type: SEGMENT_TYPE.AI_GENERATED,
    badge: "New",
  },
];
import {
  Users,
  PieChart,
  Activity,
  CheckCircle2,
  Database,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";

export default function SegmentsPage() {
  usePageTitle("Audiences");
  const navigate = useNavigate();
  const {
    segments,
    segmentsLoading,
    segmentsError,
    deleteSegment,
    getSegmentStatistics,
    performRequest,
  } = useApp();
  const [segmentStats, setSegmentStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(new Set());
  const [totals, setTotals] = useState({
    reach: 0,
    impressions: 0,
    activeAudiences: 0,
    dataSources: 0,
  });
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const requestedStatsRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await performRequest("/statistics/totals");
        if (!cancelled && data) {
          setTotals({
            reach: data.reach ?? 0,
            impressions: data.impressions ?? 0,
            activeAudiences: data.activeAudiences ?? 0,
            dataSources: data.dataSources ?? 0,
          });
        }
      } catch (err) {
        console.error("Failed to load /statistics/totals", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [performRequest]);

  const handleCreate = () => navigate("/dashboard/segments/new");
  const handleEdit = (segment) =>
    navigate(`/dashboard/segments/${segment.uuid}/edit`);

  const handleDelete = async (segment) => {
    const confirmed =
      typeof window !== "undefined"
        ? window.confirm(`Delete segment "${segment.name}"?`)
        : true;
    if (confirmed) {
      try {
        await deleteSegment(segment.uuid);
        setSegmentStats((prev) => {
          const next = { ...prev };
          delete next[segment.uuid];
          return next;
        });
        requestedStatsRef.current.delete(segment.uuid);
      } catch (error) {
        console.error("Failed to delete segment", error);
        if (typeof window !== "undefined") {
          window.alert("Failed to delete segment. Please try again.");
        }
      }
    }
  };

  const loadSegmentStatistics = useCallback(
    async (segmentUuid) => {
      if (
        !segmentUuid ||
        loadingStats.has(segmentUuid) ||
        requestedStatsRef.current.has(segmentUuid)
      ) {
        return;
      }

      requestedStatsRef.current.add(segmentUuid);
      setLoadingStats((prev) => new Set(prev).add(segmentUuid));
      try {
        const stats = await getSegmentStatistics(segmentUuid);
        setSegmentStats((prev) => ({
          ...prev,
          [segmentUuid]: stats,
        }));
      } catch (error) {
        console.error(
          `Failed to load statistics for segment ${segmentUuid}`,
          error,
        );
        setSegmentStats((prev) => ({
          ...prev,
          [segmentUuid]: { vasts: 0, totalImpressions: 0 },
        }));
      } finally {
        setLoadingStats((prev) => {
          const next = new Set(prev);
          next.delete(segmentUuid);
          return next;
        });
      }
    },
    [getSegmentStatistics, loadingStats],
  );

  useEffect(() => {
    if (segments.length > 0 && !segmentsLoading) {
      segments.forEach((segment, index) => {
        if (segment.uuid && !requestedStatsRef.current.has(segment.uuid)) {
          setTimeout(() => {
            loadSegmentStatistics(segment.uuid);
          }, index * 50);
        }
      });
    }
  }, [segments, segmentsLoading]);

  const activeType = useMemo(
    () => TYPE_TABS.find((t) => t.id === activeTab)?.type ?? null,
    [activeTab],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return segments.filter((s) => {
      const segType = s.type ?? DEFAULT_SEGMENT_TYPE;
      if (activeType !== null && segType !== activeType) return false;
      if (!q) return true;
      return (
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
      );
    });
  }, [segments, search, activeType]);

  const tabCounts = useMemo(() => {
    const counts = { all: segments.length };
    for (const tab of TYPE_TABS) {
      if (tab.type === null) continue;
      counts[tab.id] = segments.filter(
        (s) => (s.type ?? DEFAULT_SEGMENT_TYPE) === tab.type,
      ).length;
    }
    return counts;
  }, [segments]);

  const compactFormatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
  const formatCompact = (n) => compactFormatter.format(Number(n) || 0);

  return (
    <section>
      <div className="mb-[26px] grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Total audiences"
          value={formatCompact(segments.length)}
          trend="Across all types"
          icon={<Users size={22} strokeWidth={1.8} />}
          accent="violet"
        />
        <StatCard
          label="Total reach"
          value={formatCompact(totals.reach)}
          trend="Households"
          icon={<PieChart size={22} strokeWidth={1.8} />}
          accent="violet"
        />
        <StatCard
          label="Avg. household reach"
          value={formatCompact(
            segments.length > 0
              ? Math.round(totals.reach / segments.length)
              : 0,
          )}
          trend="Per audience"
          icon={<Activity size={22} strokeWidth={1.8} />}
          accent="violet"
        />
        <StatCard
          label="Active in campaigns"
          value={formatCompact(totals.activeAudiences)}
          trend="Audiences"
          icon={<CheckCircle2 size={22} strokeWidth={1.8} />}
          accent="green"
        />
        <StatCard
          label="Data sources"
          value={formatCompact(totals.dataSources)}
          trend="Connected"
          icon={<Database size={22} strokeWidth={1.8} />}
          accent="blue"
        />
      </div>

      <nav className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-(--border-subtle)">
        {TYPE_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id] ?? 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 whitespace-nowrap border-0 bg-transparent px-4 py-[11px] text-[13.5px] transition-colors ${
                isActive
                  ? "font-bold text-(--brand-primary)"
                  : "font-semibold text-(--text-secondary) hover:text-(--text-primary)"
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className="rounded-md bg-(--pastel-violet) px-[7px] py-[2px] text-[10px] font-bold text-(--brand-primary)">
                  {tab.badge}
                </span>
              )}
              {count > 0 && !tab.badge && (
                <span className="text-[12px] font-semibold text-(--text-muted)">
                  {count}
                </span>
              )}
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-[2.5px] rounded-[2px] bg-(--brand-primary)"
                />
              )}
            </button>
          );
        })}
      </nav>

      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search audiences..."
        />
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
        >
          <Plus size={16} strokeWidth={2.5} />
          Create audience
        </button>
      </div>

      {segmentsError && (
        <Card className="mb-4">
          <h3 className="text-base font-semibold text-(--text-primary)">
            Unable to load segments
          </h3>
          <p className="mt-1 text-sm text-(--text-secondary)">
            Please refresh the page or try again later.
          </p>
        </Card>
      )}

      {segmentsLoading && segments.length === 0 ? (
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading audiences…</p>
        </Card>
      ) : (
        <DataTable>
          <thead>
            <tr className={DATA_TABLE_CLASSES.headRow}>
              <th className={`w-10 ${DATA_TABLE_CLASSES.th}`}>
                <input type="checkbox" aria-label="Select all" />
              </th>
              <th className={DATA_TABLE_CLASSES.th}>Audience name</th>
              <th className={DATA_TABLE_CLASSES.th}>Type</th>
              <th className={DATA_TABLE_CLASSES.th}>Reach</th>
              <th className={DATA_TABLE_CLASSES.th}>Impressions</th>
              <th className={DATA_TABLE_CLASSES.th}>Status</th>
              <th className={DATA_TABLE_CLASSES.th}>Updated ↓</th>
              <th className={`w-12 ${DATA_TABLE_CLASSES.thRight}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="8" className={DATA_TABLE_CLASSES.emptyCell}>
                  {search
                    ? "No audiences match your search."
                    : activeTab !== "all"
                      ? "No audiences in this category yet."
                      : "No audiences yet. Build your first audience to get started."}
                </td>
              </tr>
            ) : (
              filtered.map((segment) => {
                const lastUpdated = segment.updatedAt || segment.createdAt;
                const relativeUpdated = lastUpdated
                  ? formatDistanceToNow(lastUpdated)
                  : "";
                const reach =
                  segmentStats[segment.uuid]?.vasts ?? segment.vasts ?? 0;
                const impressions =
                  segmentStats[segment.uuid]?.totalImpressions ??
                  segment.totalImpressions ??
                  0;
                const idShort = segment.uuid
                  ? `AUD-${segment.uuid.slice(0, 4).toUpperCase()}`
                  : "AUD-—";
                const parsed = getRowIcon(segment, DEFAULT_AUDIENCE_ICON);
                const Icon = parsed.Icon;
                const displayName = nameOr(
                  parsed.displayName,
                  "Untitled audience",
                );
                return (
                  <tr
                    key={segment.uuid}
                    onDoubleClick={() => handleEdit(segment)}
                    className={`${DATA_TABLE_CLASSES.bodyRow} cursor-pointer`}
                  >
                    <td className={DATA_TABLE_CLASSES.td}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${displayName}`}
                      />
                    </td>
                    <td className={DATA_TABLE_CLASSES.td}>
                      <div className="flex items-center gap-[13px]">
                        <RowIcon icon={Icon} />
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-bold text-(--text-primary)">
                            {displayName}
                          </div>
                          <div className="mt-1 text-[12px] font-medium text-(--text-muted)">
                            ID: {idShort}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className={`${DATA_TABLE_CLASSES.td} font-semibold text-(--text-secondary)`}
                    >
                      {(() => {
                        const type = segment.type ?? DEFAULT_SEGMENT_TYPE;
                        const TypeIcon = getSegmentTypeIcon(type);
                        return (
                          <span className="inline-flex items-center gap-2">
                            <TypeIcon size={16} strokeWidth={1.8} />
                            {getSegmentTypeLabel(type)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={DATA_TABLE_CLASSES.td}>
                      {loadingStats.has(segment.uuid) ? (
                        <span className="text-(--text-muted)">…</span>
                      ) : (
                        <div className="text-[13.5px] font-bold text-(--text-primary)">
                          <AnimatedNumber value={reach} />
                        </div>
                      )}
                    </td>
                    <td className={DATA_TABLE_CLASSES.td}>
                      {loadingStats.has(segment.uuid) ? (
                        <span className="text-(--text-muted)">…</span>
                      ) : (
                        <div className="text-[13.5px] font-bold text-(--text-primary)">
                          <AnimatedNumber value={impressions} />
                        </div>
                      )}
                    </td>
                    <td className={DATA_TABLE_CLASSES.td}>
                      <Badge variant="success">Active</Badge>
                    </td>
                    <td
                      className={`${DATA_TABLE_CLASSES.td} whitespace-nowrap text-[13px] font-medium text-(--text-secondary)`}
                    >
                      {relativeUpdated ? `${relativeUpdated} ago` : "just now"}
                    </td>
                    <td className={DATA_TABLE_CLASSES.tdRight}>
                      <ActionMenu
                        ariaLabel={`Actions for ${displayName}`}
                        items={[
                          {
                            label: "Edit",
                            icon: Pencil,
                            onClick: () => handleEdit(segment),
                          },
                          {
                            label: "Delete",
                            icon: Trash2,
                            danger: true,
                            onClick: () => handleDelete(segment),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </DataTable>
      )}

      <div className="mt-[22px] text-[13px] text-(--text-muted)">
        Showing {filtered.length} of {segments.length} audiences
      </div>
    </section>
  );
}
